import { useEffect, useState, useMemo, useRef } from 'react';

import { addError } from '@shared/utils/logger';
import { useStandardHotelRoomPriceMapping } from './useListingPriceMapping';
import type { JoyHotelAccommodationListingDTO } from '@shared/types/Accommodations.types';
import { isValidProperty, transformPropertiesToListings } from '@shared/utils/accommodations/AccommodationTiles';
import type { SearchExtendedAccommodationPropertiesV2Payload } from '@graphql/generated-base';
import { useAccommodationsConsolePropertiesV2Query } from '../AccommodationsConsole.graphql.generated';
import { FetchStrategy } from '@graphql/generated-base';
import { useListingsPanelTelemetry } from '../components/ListingsPanel/ListingsPanel.telemetry';
import { isBotClient } from '@shared/utils/botDetection/isBotClient';
import { PriceSource } from '@shared/constants/accommodations';
import type { CashFundConfig } from '@shared/utils/cashFund/cashFund.types';
import { buildWalletSpendQueryVariables } from './walletSpendQueryVariables';

interface UseBackgroundPricingRefreshArgs
  extends Readonly<{
    isFetchingListings: boolean;
    hasReceivedRegionalData: boolean;
    regionalHotelsPayload: SearchExtendedAccommodationPropertiesV2Payload;
    locationName?: string;
    /** Must match what the first fetch sent, or this refresh replaces the tiles with split-less ones. */
    cashFund?: CashFundConfig;
  }> {}

interface BackgroundPricingRefreshResult
  extends Readonly<{
    isLoading: boolean;
    error: Error | undefined;
    listings: ReadonlyArray<JoyHotelAccommodationListingDTO>;
  }> {}

export const useBackgroundPricingRefresh = (args: UseBackgroundPricingRefreshArgs): BackgroundPricingRefreshResult => {
  const { regionalHotelsPayload, isFetchingListings, hasReceivedRegionalData, locationName, cashFund } = args;

  const [listings, setListings] = useState<readonly JoyHotelAccommodationListingDTO[]>([]);

  const telemetry = useListingsPanelTelemetry();
  const searchStartTimeRef = useRef<number | null>(null);

  const mapHotelRoomPrice = useStandardHotelRoomPriceMapping();

  const payload = useMemo(() => {
    if (!regionalHotelsPayload?.customSearchParams) {
      return null;
    }

    return {
      eventHandle: regionalHotelsPayload.eventHandle,
      customSearchParams: {
        ...regionalHotelsPayload.customSearchParams,
        radiusKM: 5,
        useRealtimePricing: true,
        fetchStrategy: FetchStrategy.PREFER_VENDOR,
      },
    };
  }, [regionalHotelsPayload]);

  const shouldSkipQuery = !payload || isFetchingListings || !hasReceivedRegionalData || isBotClient();

  useEffect(() => {
    setListings([]);
    searchStartTimeRef.current = Date.now();
  }, [regionalHotelsPayload]);

  const { data, error, loading } = useAccommodationsConsolePropertiesV2Query({
    batchMode: 'off',
    skip: shouldSkipQuery,
    fetchPolicy: 'network-only',
    variables: { payload: payload!, ...buildWalletSpendQueryVariables(cashFund) },
  });

  useEffect(() => {
    const properties = data?.searchExtendedAccommodationProperties?.properties;
    if (!properties) {
      return;
    }

    const validProperties = properties.filter(isValidProperty);
    // PREFER_VENDOR with useRealtimePricing true, so these prices come from a realtime vendor fetch
    // and replace the snapshot prices the first query rendered.
    const newListings = transformPropertiesToListings(validProperties, mapHotelRoomPrice, PriceSource.LIVE);
    setListings(newListings);

    if (searchStartTimeRef.current !== null) {
      telemetry.destinationSearched({
        googlePlaceId: regionalHotelsPayload?.customSearchParams?.location?.context?.googlePlaceId,
        locationName,
        responseTimeMs: Date.now() - searchStartTimeRef.current,
        resultCount: validProperties.length,
      });
      searchStartTimeRef.current = null;
    }
  }, [data, mapHotelRoomPrice]); // eslint-disable-line react-hooks/exhaustive-deps -- regionalHotelsPayload and locationName are only used for telemetry context

  useEffect(() => {
    if (error) {
      addError(error, {
        errorMessage: error.message,
        context: 'useBackgroundPricingRefresh',
        source: 'Background pricing refresh failed (AccommodationsConsole)',
      });
    }
  }, [error]);

  return {
    error,
    listings,
    isLoading: loading,
  };
};
